import { PublicRoute } from '../../router/restricted-routes-wrapper.tsx';
import { ReactNode, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import { BrandingProvider } from '@utils/providers/branding-context.tsx';
import PageErrorBoundary from '@components/layout/page-error-boundary.tsx';
import { Accessibility, ActivitySquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IS_ON_PREM } from '@utils/constants/app-constants.ts';
import { PATHS } from '../../router/paths.ts';

interface Props {
  children: ReactNode;
}

export default function PublicPageWrapper(props: Props) {
  const { getColour, setUserColors } = useThemeContext();
  const { t } = useTranslation();

  useEffect(() => {
    setUserColors({});
  }, [setUserColors]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        mainContainer: {
          backgroundColor: getColour('baseLayer')
        }
      }),
    [getColour]
  );

  return (
    <PublicRoute>
      <BrandingProvider>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[10000] focus:px-4 focus:py-2 focus:rounded-2xl focus:shadow-pop focus:bg-app focus:text-primary focus:outline-2 focus:outline-primary"
        >
          {t('skip-to-content')}
        </a>
        <main
          id="main-content"
          className={cn('h-screen w-full relative', css(styles.mainContainer))}
        >
          {!IS_ON_PREM && (
            <div className="absolute top-3 right-3 z-50 flex items-center gap-2">
              <a
                href={PATHS().ACCESSIBILITY}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-2xl transition-colors hover:bg-black/5 dark:hover:bg-white/10 text-xs font-medium"
                style={{ color: getColour('icon') }}
                aria-label={t('accessibility-statement')}
              >
                <Accessibility className="h-4 w-4" />
                <span>{t('accessibility-statement')}</span>
              </a>
              <a
                href={PATHS().STATUS}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-2xl transition-colors hover:bg-black/5 dark:hover:bg-white/10 text-xs font-medium"
                style={{ color: getColour('icon') }}
                aria-label={t('status')}
              >
                <ActivitySquare className="h-4 w-4" />
                <span>{t('status')}</span>
              </a>
            </div>
          )}
          <PageErrorBoundary>{props.children}</PageErrorBoundary>
        </main>
      </BrandingProvider>
    </PublicRoute>
  );
}
